import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
// Не из индекса фичи: индекс тянет экран игры, а игра — отдельным чанком (routesConfig). Здесь — только данные хаба.
import { useStoriesHubQuery } from '@features/Stories/model/queries';
import { trackEvent } from '@shared/analytics';
import { buildStoryPlayPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { PlayIcon } from '@shared/ui';

// Карта с движком — своим чанком (тем же, что у игры): основной чанк главной её не несёт.
const MapPreview = lazy(() => import('@features/Stories/ui/MapPreview'));

/**
 * Игра «Выживи в Лондоне» на главной — баннер под тренировкой (решение владельца 2026-10-22: «киллер фича, чтобы
 * удерживать»; вариант «Баннер под тренировкой»). Слева — живая карта игры, справа — что это и одна кнопка: начатая
 * история или бой — «Продолжить» (сразу туда, бесплатно), иначе «Играть» — на карту игры, где цены и выбор. Миссия
 * дня — строкой плашки «На сегодня» (MissionRow). Хаб не загрузился — без счётчиков, кнопка всё равно ведёт в игру.
 */
export function GameBanner({ phone }: { phone: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useStoriesHubQuery();
  const hub = query.data;
  const daily = useMemo(
    () => (hub ? { location: hub.daily.location, character: hub.daily.character, done: hub.daily.done } : null),
    [hub],
  );
  // Начатая миссия дня — на своей плашке; здесь — история и бой.
  const activeRun = hub ? (hub.stories.find((s) => s.active_run_id)?.active_run_id ?? hub.boss.active_run_id) : null;
  const endings = hub ? hub.stories.flatMap((s) => s.endings) : [];
  const unlocked = endings.filter((e) => e.unlocked).length;

  const open = () => {
    trackEvent('home_game_clicked', { action: activeRun ? 'resume' : 'open', mission_done: hub?.daily.done ?? null });
    navigate(activeRun ? buildStoryPlayPath(activeRun) : paths.stories);
  };

  return (
    <section aria-labelledby="dash-game-title" className="dash-game overflow-hidden rounded-card border border-solid border-[#3a3a40] bg-card">
      {/* Раскладка — по ширине самого баннера (контейнер .dash-game): на широкой колонке он в две трети главной. */}
      <div className="dash-game-grid">
        {/* Карта — только картинка: нажатия ловит ссылка поверх, у самой карты их нет (перетаскивание на главной не нужно). */}
        <Link
          to={paths.stories}
          tabIndex={-1}
          aria-hidden
          onClick={() => trackEvent('home_game_clicked', { action: 'map', mission_done: hub?.daily.done ?? null })}
          className={cn('dash-game-map relative block overflow-hidden bg-[#3f86d9]', phone ? 'h-[168px]' : 'h-[200px]')}
        >
          {/* Слой карты вне потока: высоту баннера задаёт колонка текста, а не холст. Раньше карте шли и `relative` от
              PixelWorld, и `absolute inset-0` отсюда — cn их не сливает, побеждал `relative`; холст по ResizeObserver
              брал высоту обёртки и держал её — баннер только рос (владелец 2026-10-26, ТЗ перепланировки P0-2). */}
          <div className="absolute inset-0">
            <Suspense fallback={<div className="h-full w-full animate-skeleton bg-[#2f6fb8]" />}>
              <MapPreview daily={daily} className="pointer-events-none h-full w-full" />
            </Suspense>
          </div>
        </Link>
        <div className={cn('flex min-w-0 flex-col', phone ? 'gap-3 p-4' : 'gap-4 px-8 py-7')}>
          <div>
            <div className="text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-accent">{t('stories.hub.kicker')}</div>
            <h2
              id="dash-game-title"
              className={cn('mt-1.5 font-serif font-medium tracking-[-0.01em] text-fg', phone ? 'text-[24px] leading-[30px]' : 'text-[30px] leading-[36px]')}
            >
              {t('stories.hub.title')}
            </h2>
            <p className={cn('mt-1.5 text-muted', phone ? 'line-clamp-3 text-[14px] leading-5' : 'line-clamp-2 text-[15px] leading-[22px]')}>
              {t('stories.hub.sub')}
            </p>
          </div>
          <div className={cn('mt-auto flex flex-wrap items-center gap-x-5 gap-y-2', phone && 'flex-col items-stretch')}>
            {/* Кнопка — светлая «пиксельная», как рамка карты игры: оранжевая на главной одна — «Тренироваться». */}
            <button
              type="button"
              onClick={open}
              className={cn(
                // Рамка «пиксельная» (clip-path) срезала бы обычный контур фокуса — фокус рисуем внутри.
                'lx-px-sm inline-flex items-center justify-center gap-2.5 bg-[#f4ecd8] font-semibold text-[#141416] transition-colors hover:bg-white focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_3px_#F36A1C]',
                phone ? 'h-12 w-full text-[16px]' : 'h-[52px] px-7 text-[16px]',
              )}
            >
              <PlayIcon className="h-3.5 w-3.5 flex-none" aria-hidden />
              {activeRun ? t('dashboard.home.game.resume') : t('dashboard.home.game.play')}
            </button>
            {hub && (endings.length > 0 || hub.boss.open_mistakes > 0) && (
              <p className={cn('text-[13px] leading-[18px] text-muted', phone && 'text-center')}>
                {endings.length > 0 && t('dashboard.home.game.endings', { n: unlocked, total: endings.length })}
                {/* Точка остаётся в конце строки, а не начинает следующую (ТЗ перепланировки, P1-4). */}
                {endings.length > 0 && hub.boss.open_mistakes > 0 && '\u00a0· '}
                {hub.boss.open_mistakes > 0 && t('dashboard.home.game.boss', { count: hub.boss.open_mistakes })}
              </p>
            )}
            {/* Пока грузится хаб — место под строку счётчиков (своей строкой, как она сама): иначе после загрузки баннер
                вырастал на ~26 px и сдвигал блок под собой (ТЗ перепланировки, P1-6). */}
            {query.isPending && <span aria-hidden className="block h-[18px] w-full" />}
          </div>
        </div>
      </div>
    </section>
  );
}
